import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { MessageSquare, Save, Smartphone, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme, themes } from '../ThemeProvider';
import { API_URL } from '../../lib/api-client';

type SmsMethod = 'composer' | 'silent';

export function SmsMethodSettings() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const [smsMethod, setSmsMethod] = useState<SmsMethod>('composer');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    fetchSmsMethod();
  }, []);

  const fetchSmsMethod = async () => {
    try {
      setFetching(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/settings/sms-method`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setSmsMethod(data.smsMethod || 'composer');
      }
    } catch (error) {
      console.error('Failed to fetch SMS method:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleUpdateSmsMethod = async (newMethod: SmsMethod) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/settings/sms-method`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ smsMethod: newMethod }),
      });

      const data = await response.json();
      if (data.success) {
        setSmsMethod(newMethod);
        toast.success(`SMS method updated to ${newMethod === 'composer' ? 'SMS Composer' : 'Silent SMS'}`);
      } else {
        toast.error(data.message || 'Failed to update SMS method');
      }
    } catch (error) {
      console.error('Failed to update SMS method:', error);
      toast.error('Failed to update SMS method');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" style={{ color: currentTheme.primary }} />
          SMS Invite Method
        </CardTitle>
        <CardDescription>
          Choose how users send SMS invites in the mobile app (Expo only)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Method Display */}
        <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">Current Method:</span>
                <Badge className={smsMethod === 'composer' ? 'bg-green-500' : 'bg-orange-500'}>
                  {smsMethod === 'composer' ? 'SMS Composer' : 'Silent SMS'}
                </Badge>
              </div>
              <p className="text-sm text-gray-600">
                {smsMethod === 'composer' 
                  ? 'Users see and approve SMS before sending (Recommended)'
                  : 'SMS sent automatically without user confirmation (Android only)'}
              </p>
            </div>
          </div>
        </div>

        {/* SMS Composer Option */}
        <div 
          className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
            smsMethod === 'composer' 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
          onClick={() => smsMethod !== 'composer' && handleUpdateSmsMethod('composer')}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${smsMethod === 'composer' ? 'bg-green-500' : 'bg-gray-200'}`}>
                <Smartphone className={`h-5 w-5 ${smsMethod === 'composer' ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  SMS Composer
                  <Badge className="bg-green-500 text-white">Recommended</Badge>
                </h3>
                <p className="text-sm text-gray-600">Industry standard • Transparent • Works everywhere</p>
              </div>
            </div>
            {smsMethod === 'composer' && (
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            )}
          </div>

          <div className="space-y-2 ml-11">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
              <span className="text-sm">Works on iOS & Android</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
              <span className="text-sm">User has full transparency and control</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
              <span className="text-sm">No Google Play Store policy violations</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
              <span className="text-sm">Used by WhatsApp, Facebook, LinkedIn</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
              <span className="text-sm">Builds user trust</span>
            </div>
          </div>

          {smsMethod !== 'composer' && (
            <Button
              className="mt-4 ml-11"
              onClick={(e) => {
                e.stopPropagation();
                handleUpdateSmsMethod('composer');
              }}
              disabled={loading}
            >
              Switch to SMS Composer
            </Button>
          )}
        </div>

        {/* Silent SMS Option */}
        <div 
          className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
            smsMethod === 'silent' 
              ? 'border-orange-500 bg-orange-50' 
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
          onClick={() => smsMethod !== 'silent' && handleUpdateSmsMethod('silent')}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${smsMethod === 'silent' ? 'bg-orange-500' : 'bg-gray-200'}`}>
                <MessageSquare className={`h-5 w-5 ${smsMethod === 'silent' ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  Silent SMS
                  <Badge className="bg-orange-500 text-white">Android Only</Badge>
                </h3>
                <p className="text-sm text-gray-600">Automated • No user confirmation • Risky</p>
              </div>
            </div>
            {smsMethod === 'silent' && (
              <CheckCircle2 className="h-6 w-6 text-orange-500" />
            )}
          </div>

          <div className="space-y-2 ml-11">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
              <span className="text-sm">Fully automated (no user interaction)</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
              <span className="text-sm">Can send to many contacts at once</span>
            </div>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5" />
              <span className="text-sm font-medium text-orange-700">Android ONLY (iOS impossible)</span>
            </div>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5" />
              <span className="text-sm font-medium text-orange-700">Requires Expo Development Build</span>
            </div>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" />
              <span className="text-sm font-medium text-red-700">High Google Play Store rejection risk</span>
            </div>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" />
              <span className="text-sm font-medium text-red-700">User doesn't see what's sent (trust issues)</span>
            </div>
          </div>

          {smsMethod !== 'silent' && (
            <Button
              className="mt-4 ml-11 bg-orange-500 hover:bg-orange-600"
              onClick={(e) => {
                e.stopPropagation();
                handleUpdateSmsMethod('silent');
              }}
              disabled={loading}
            >
              Switch to Silent SMS
            </Button>
          )}
        </div>

        {/* Warning for Silent SMS */}
        {smsMethod === 'silent' && (
          <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold text-red-900">⚠️ Warning: Not Recommended for Production</p>
                <p className="text-sm text-red-700">
                  Silent SMS may violate Google Play Store policies and damage user trust. 
                  Only works on Android (excluding 50% of users on iOS). 
                  Consider switching back to SMS Composer for better user experience and compliance.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}